import { FC, PropsWithChildren } from 'react'
import { Box, styled, typographyClasses } from '@mui/material'
import { ReviewModeLayout, TestTakingLayout, InteractionLayoutFooter } from './components'
import { GridLayout } from '../GridLayout'
import { scrollBarMixin, HIGHLIGHT_CONTAINER_QUESTION, tableStylingMixin } from '@app/constants'
import { useSnapshot } from 'valtio'
import { ScoringState } from '@app/storage'

interface InteractionLayoutProps {
  reviewMode?: boolean
  itemId?: string
  twoColumn?: boolean
}

const InteractionLayout: FC<PropsWithChildren<InteractionLayoutProps>> = ({
  children,
  itemId,
  twoColumn,
  reviewMode = false,
}) => {
  const { isQuestionPreviewMode } = useSnapshot(ScoringState)

  return (
    <InteractionLayoutWrapper
      aria-label='Question block'
      twoColumn={twoColumn}
      // Disable test tools in review mode (Mark for Review, Highlight, Strikethrough).
      as={reviewMode ? ReviewModeLayout : TestTakingLayout}
      itemId={!reviewMode && itemId}
    >
      <InteractionWrapper key={itemId}>
        <GridLayout horizontal={twoColumn} id={HIGHLIGHT_CONTAINER_QUESTION}>
          {children}
        </GridLayout>
      </InteractionWrapper>

      {!isQuestionPreviewMode && <InteractionLayoutFooter reviewMode={reviewMode} />}
    </InteractionLayoutWrapper>
  )
}

// TODO: Should we stick to absolute value for interaction max-width
// or use relative?
const InteractionLayoutWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'twoColumn',
})<{ twoColumn?: boolean; itemId?: string }>(
  ({ theme, twoColumn }) => `
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: ${theme.shape.borderRadius * 1.5}px;
  box-shadow: ${theme.shadows[2]};
  flex: 1 1 50%;
  overflow: auto;
  max-width: ${twoColumn ? '683px' : '860px'};
  background-color: ${theme.palette.common.white};

  ${[theme.breakpoints.down('tablet')]} {
    /* a11y: grow to content, stop nested vertical scroll at high zoom (SC 1.4.10) */
    overflow: visible;
  }

  ${[theme.breakpoints.down('mobile')]} {
    flex: 1 1 auto;
  }
`,
)

const InteractionWrapper = styled(Box)(
  ({ theme }) => `
  padding: ${theme.spacing(8, 4)};
  margin: ${theme.spacing(0, 2)};
  flex-grow: 1;
  overflow: auto;

  ${scrollBarMixin(theme)};
  ${tableStylingMixin(theme)}

  ${[theme.breakpoints.down('tablet')]} {
    /* a11y: release vertical scroll; keep wide tables/zoom images scrolling locally, not page-wide (SC 1.4.10) */
    overflow-x: auto;
    overflow-y: visible;
  }

  ${[theme.breakpoints.down('mobile')]} {
    padding: ${theme.spacing(4, 2)};
  }

  & p.${typographyClasses.root} {
    margin-bottom: ${theme.spacing(4)};
    ${[theme.breakpoints.down('mobile')]} {
      margin-bottom: ${theme.spacing(3)};
    }
  }
`,
)

export default InteractionLayout
